import { API_URL } from "../config/api";

export interface RoomLocation {
  room_id: string;
  room_name: string;
  floor_id: string;
  floor_name: string;
  site_id: string;
  site_name: string;
  full_path: string;
}

/**
 * Get all sites for a project
 */
export const getProjectSites = async (token: string, projectId: string): Promise<ProjectSiteItemInterface[]> => {
  try {
    const response = await fetch(`${API_URL}/data-center/projects/${projectId}/sites`, {
      headers: {
        'content-type': 'application/json',
        'authorization': 'Bearer ' + token
      }
    });

    if (!response.ok) {
      console.warn(`Sites API returned ${response.status}`);
      return [];
    }

    const data = await response.json();
    console.log('Sites API response:', data);
    
    // Handle different response formats
    if (Array.isArray(data)) {
      return data;
    }
    if (data?.results && Array.isArray(data.results)) {
      return data.results;
    }
    
    return [];
  } catch (error) {
    console.error('Error fetching sites:', error);
    return [];
  }
};

/**
 * Get all floors for a site
 */
export const getSiteFloors = async (token: string, projectId: string, siteId: string): Promise<FloorInterface[]> => {
  try {
    const response = await fetch(`${API_URL}/data-center/projects/${projectId}/sites/${siteId}/floors`, {
      headers: {
        'content-type': 'application/json',
        'authorization': 'Bearer ' + token
      }
    });

    if (!response.ok) {
      console.warn(`Floors API returned ${response.status}`);
      return [];
    }

    const data = await response.json();
    console.log('Floors API response:', data);
    
    // Handle different response formats
    if (Array.isArray(data)) {
      return data;
    }
    if (data?.results && Array.isArray(data.results)) {
      return data.results;
    }
    
    return [];
  } catch (error) {
    console.error('Error fetching floors:', error);
    return [];
  }
};

/**
 * Get all rooms for a floor
 */
export const getFloorRooms = async (token: string, projectId: string, siteId: string, floorId: string): Promise<RoomInterface[]> => {
  try {
    const response = await fetch(`${API_URL}/data-center/projects/${projectId}/sites/${siteId}/floors/${floorId}/rooms`, {
      headers: {
        'content-type': 'application/json',
        'authorization': 'Bearer ' + token
      }
    });

    if (!response.ok) {
      console.warn(`Rooms API returned ${response.status}`);
      return [];
    }

    const data = await response.json();
    console.log('Rooms API response:', data);
    
    // Handle different response formats
    if (Array.isArray(data)) {
      return data;
    }
    if (data?.results && Array.isArray(data.results)) {
      return data.results;
    }
    
    return [];
  } catch (error) {
    console.error('Error fetching rooms:', error);
    return [];
  }
};

/**
 * Get all rooms for a project with location hierarchy
 */
export const getAllProjectRooms = async (token: string, projectId: string): Promise<RoomLocation[]> => {
  try {
    const rooms: RoomLocation[] = [];
    
    console.log('Loading project rooms for project:', projectId);
    
    // Get all sites
    const sites = await getProjectSites(token, projectId);
    console.log('Found sites:', sites.length);
    
    // For each site, get floors
    for (const site of sites) {
      if (!site?.id || !site?.name) continue;
      
      const floors = await getSiteFloors(token, projectId, site.id);
      console.log(`Site ${site.name}: found ${floors.length} floors`);
      
      // For each floor, get rooms
      for (const floor of floors) {
        if (!floor?.id || !floor?.name) continue;
        
        const floorRooms = await getFloorRooms(token, projectId, site.id, floor.id);
        console.log(`Floor ${floor.name}: found ${floorRooms.length} rooms`);
        
        // Add rooms with location hierarchy
        for (const room of floorRooms) {
          if (!room?.id || !room?.name) continue;
          
          rooms.push({
            room_id: room.id,
            room_name: room.name,
            floor_id: floor.id,
            floor_name: floor.name,
            site_id: site.id,
            site_name: site.name,
            full_path: `${site.name} / ${floor.name} / ${room.name}`
          });
        }
      }
    }
    
    console.log('Total rooms found:', rooms.length);
    return rooms;
    
  } catch (error) {
    console.error('Error in getAllProjectRooms:', error);
    return [];
  }
}; 